/**
 * CharterPrepPage — Phase 1 individual preparation session (standalone route).
 *
 * Thin wrapper: owns the WS connection lifecycle, phase guards/navigation, and
 * the shared game shell (sidebar + navbar). The sidebar "Operations" content
 * and the chat body are the reusable <CharterPrepSidebar /> / <CharterPrepBody />
 * components, which are also embedded inside the in-game PlayerGame screen.
 */
import { useEffect } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { AlertCircle, Loader2, Sparkles } from 'lucide-react'
import { SidebarProvider } from '@/components/ui/sidebar'
import OGESidebar from '@/components/OGE/Sidebar/OGESidebar'
import OGESidebarHeader from '@/components/OGE/Sidebar/OGESidebarHeader'
import Navbar from '@/components/Layout/Navbar'
import { Badge } from '@/components/ui/badge'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { fetchCharterSession } from '../../redux/asyncThunks/charterThunks'
import {
  selectCharterSession,
  selectCharterEnabled,
  selectCharterConfig,
  selectPrepIsConnected,
  selectPrepStatus,
} from '../../redux/selectors/charterSelectors'
import {
  connectCharterPrepWS,
  disconnectCharterPrepWS,
} from '../../redux/asyncThunks/charterWsThunks'
import { CharterPrepBody } from '../../components/Shell/CharterPrepBody'
import { CharterPrepSidebar } from '../../components/Shell/CharterPrepSidebar'
import { CharterCollaborationPanel } from '../../components/Shared/CharterCollaborationPanel'
import ROUTES from '@/routes/routes'
import { getAccessToken } from '@/utils/authUtils'
import { useMyPrepStatus } from '../../hooks/useMyPrepStatus'

export default function CharterPrepPage() {
  const { roomId = '' } = useParams<{ roomId: string }>()
  const [searchParams] = useSearchParams()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const session = useAppSelector(selectCharterSession)
  const config = useAppSelector(selectCharterConfig)
  const charterEnabled = useAppSelector(selectCharterEnabled)
  const isConnected = useAppSelector(selectPrepIsConnected)
  const prepStatus = useAppSelector(selectPrepStatus)

  const currentUser = useAppSelector((s) => s.user.user)
  const userName =
    currentUser?.name || currentUser?.email?.split('@')[0] || 'You'
  const isCharterClass = useAppSelector(
    (s) => s.room.currentRoom?.is_charter_only === true
  )

  const inviteCode = searchParams.get('inviteCode') ?? ''
  // Prep can start before the student is on a team; the team then comes from
  // the session once the professor places them.
  const urlTeamId = searchParams.get('teamId') ?? ''
  const teamId = urlTeamId || (session?.team_id ? String(session.team_id) : '')
  const myPrepStatus = useMyPrepStatus(roomId, !teamId)
  const placed = Boolean(urlTeamId) || myPrepStatus?.has_team === true
  const awaitingTeam = !urlTeamId && myPrepStatus?.has_team === false

  // The session endpoint 404s until the student is placed.
  useEffect(() => {
    if (placed) dispatch(fetchCharterSession({ roomId }))
  }, [dispatch, roomId, placed])

  useEffect(() => {
    const token = getAccessToken()
    if (!token || !roomId || !inviteCode) return
    dispatch(
      connectCharterPrepWS({ jwtToken: token, roomId, inviteCode, teamId })
    )
    return () => {
      dispatch(disconnectCharterPrepWS())
    }
  }, [dispatch, roomId, inviteCode, teamId])

  useEffect(() => {
    if (prepStatus === 'phase2_ready' || prepStatus === 'phase2_unlocked') {
      navigate(
        ROUTES.STUDENT.CHARTER_GROUP(roomId) +
          `?inviteCode=${inviteCode}&teamId=${teamId}`
      )
    }
  }, [prepStatus, navigate, roomId, inviteCode, teamId])

  // ── Guards ──────────────────────────────────────────────────────────────
  // A Charter Class has neither a waiting room nor a game screen, so the
  // "leave prep" destinations differ by room kind.
  const leaveTarget = isCharterClass
    ? ROUTES.STUDENT.CHARTER_CLASS(roomId, inviteCode)
    : ROUTES.STUDENT.WAITING_ROOM(roomId)
  const lockedTarget = isCharterClass
    ? ROUTES.STUDENT.CHARTER_CLASS(roomId, inviteCode)
    : ROUTES.STUDENT.GAME(roomId)

  // Navigation belongs in an effect — calling navigate() during render is a
  // side-effect-in-render and React warns on it.
  useEffect(() => {
    if (config && !charterEnabled) {
      navigate(leaveTarget, { replace: true })
      return
    }
    if (session?.status === 'locked') {
      navigate(lockedTarget, { replace: true })
      return
    }
    if (session?.phase === 'phase2_group' || session?.phase === 'ratified') {
      navigate(
        ROUTES.STUDENT.CHARTER_GROUP(roomId) +
          `?inviteCode=${inviteCode}&teamId=${teamId}`,
        { replace: true }
      )
    }
  }, [
    config,
    charterEnabled,
    session?.status,
    session?.phase,
    leaveTarget,
    lockedTarget,
    navigate,
    roomId,
    inviteCode,
    teamId,
  ])

  if (
    (config && !charterEnabled) ||
    session?.status === 'locked' ||
    session?.phase === 'phase2_group' ||
    session?.phase === 'ratified'
  ) {
    return null
  }
  if (!inviteCode) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <div className='flex items-center gap-2 text-red-600'>
          <AlertCircle className='h-5 w-5' />
          <p className='text-sm'>
            Missing session parameters. Please rejoin the room from your waiting
            room.
          </p>
        </div>
      </div>
    )
  }

  return (
    <SidebarProvider className='h-screen'>
      <OGESidebar
        header={
          <OGESidebarHeader
            userName={userName}
            avatarSrc={currentUser?.profile_picture || ''}
            subText='Individual Prep — Phase 1'
          />
        }
        content={<CharterPrepSidebar />}
        chatContent={
          <CharterCollaborationPanel type='prep' isConnected={isConnected} />
        }
        initialPanel='game'
      />

      <div className='flex flex-col w-full h-full overflow-auto'>
        {/* Phase info bar */}
        <div className='sticky top-0 z-10 px-14 py-2 ml-[2.5rem]'>
          <div className='flex items-center justify-between p-3 bg-white rounded-md shadow'>
            <div className='flex items-center gap-2'>
              <Sparkles className='h-5 w-5 text-indigo-500' />
              <div>
                <h3 className='text-sm font-semibold text-dark-blue'>
                  Individual Prep
                </h3>
                <p className='text-xs text-gray-500'>
                  Phase 1 — Preparation Coach
                </p>
              </div>
            </div>
            {awaitingTeam && (
              <p className='hidden text-xs text-gray-500 md:block'>
                No team yet — your answers are saved and go with you when you&apos;re placed.
              </p>
            )}
            {!isConnected && (
              <Badge
                variant='outline'
                className='text-xs text-amber-600 border-amber-300'
              >
                <Loader2 className='h-3 w-3 mr-1 animate-spin' />
                Connecting…
              </Badge>
            )}
          </div>
        </div>

        <div className='flex-1 min-h-0 flex flex-col'>
          <div className='ms-6'>
            <Navbar />
          </div>

          <div className='flex overflow-x-hidden flex-col flex-1 w-full min-w-0 min-h-0'>
            <CharterPrepBody />
          </div>
        </div>
      </div>
    </SidebarProvider>
  )
}
